import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { studentsApi, type StudentResponse } from '@/api/endpoints/students'
import Spinner from '@/components/common/Spinner'
import { useUI } from '@/stores/ui'

interface Props {
  open: boolean
  onClose: () => void
  studentId: string
}

export default function ParentContactModal({ open, onClose, studentId }: Props) {
  const addToast = useUI((state) => state.addToast)
  const [loading, setLoading] = useState(true)
  const [student, setStudent] = useState<StudentResponse | null>(null)
  const [parentName, setParentName] = useState<string | null>(null)
  const [parentEmail, setParentEmail] = useState<string | null>(null)
  const [parentPhone, setParentPhone] = useState<string | null>(null)
  const [parentAddress, setParentAddress] = useState<string | null>(null)
  const [emergencyContacts, setEmergencyContacts] = useState<string[]>([])

  useEffect(() => {
    let mounted = true
    const load = async () => {
      if (!open || !studentId) return
      setLoading(true)
      try {
        // Get student details and parent contact in parallel
        const [studentData, parentContact] = await Promise.all([
          studentsApi.get(studentId),
          studentsApi.getParentContact(studentId).catch(() => null)
        ])
        
        if (!mounted) return
        
        setStudent(studentData)
        
        // Set parent contact info from the API response
        if (parentContact) {
          setParentName(parentContact.parent_name)
          setParentEmail(parentContact.parent_email)
          setParentPhone(parentContact.parent_phone)
          setParentAddress(parentContact.parent_address)
          setEmergencyContacts(parentContact.emergency_contacts || [])
        }
      } catch (error: any) {
        console.error('Failed to load student/parent details:', error)
        if (mounted) {
          addToast({
            message: error.response?.data?.detail || 'Failed to load parent contact details',
            type: 'error',
          })
        }
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => { mounted = false }
  }, [open, studentId, addToast])

  useEffect(() => {
    if (!open) {
      setStudent(null)
      setParentName(null)
      setParentEmail(null)
      setParentPhone(null)
      setParentAddress(null)
      setEmergencyContacts([])
    }
  }, [open])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          aria-modal="true"
          role="dialog"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose()
          }}
        >
          <motion.div
            className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 hover-raise mx-4"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Parent & Emergency Contact Details</h2>
              <button className="btn-secondary text-sm focus-ring" onClick={onClose}>Close</button>
            </div>

            {loading ? (
              <div className="py-8">
                <Spinner label="Loading parent details..." />
              </div>
            ) : (
              <div className="space-y-4">
                {student && (
                  <div className="pb-4 border-b border-gray-200">
                    <div className="text-sm text-gray-600 mb-1">Student</div>
                    <div className="font-medium text-lg">
                      {student.first_name} {student.last_name}
                    </div>
                    {student.date_of_birth && (
                      <div className="text-sm text-gray-600 mt-1">
                        DOB: {new Date(student.date_of_birth).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Primary Contact</div>
                  <div className="space-y-2 pl-4">
                    {parentName ? (
                      <>
                        <div>
                          <div className="text-xs text-gray-600">Name</div>
                          <div className="font-medium">{parentName}</div>
                        </div>
                        {parentEmail && (
                          <div>
                            <div className="text-xs text-gray-600">Email</div>
                            <div>
                              <a 
                                href={`mailto:${parentEmail}`}
                                className="text-blue-600 hover:text-blue-800 underline"
                              >
                                {parentEmail}
                              </a>
                            </div>
                          </div>
                        )}
                        {parentPhone && (
                          <div>
                            <div className="text-xs text-gray-600">Cell Phone</div>
                            <div>
                              <a 
                                href={`tel:${parentPhone}`}
                                className="text-blue-600 hover:text-blue-800 underline"
                              >
                                {parentPhone}
                              </a>
                            </div>
                          </div>
                        )}
                        {parentAddress && (
                          <div>
                            <div className="text-xs text-gray-600">Address</div>
                            <div>{parentAddress}</div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-sm text-gray-500 italic">No parent contact information available</div>
                    )}
                  </div>
                </div>

                {emergencyContacts.length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">Emergency Contacts</div>
                    <div className="space-y-1 pl-4">
                      {emergencyContacts.map((contact, idx) => (
                        <div key={idx}>
                          <a 
                            href={`tel:${contact}`}
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            {contact}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(!parentName && emergencyContacts.length === 0) && (
                  <div className="text-sm text-gray-500 italic text-center py-4">
                    No contact information found for this student's parent.
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

